import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { AlertBanner, Box, Button, Group, Select, Stack, Table, Tag, Text, Title } from '@grovus/design-system'
import type { Client } from '../data/clients'
import {
  changeRole, deleteCompany, listInvites, removeMember, resendInvite, revokeInvite, type Invite, type Member, type Outcome,
} from '../data/members'
import { MEMBERS_COPY, type Lang, type MembersCopy } from '../i18n'
import { fmt } from '../lib/format'
import { can, canChangeMember, canDeleteCompany, canRemoveMember, invitableRoles, type Role } from '../lib/roles'
import { ActivityLog } from './ActivityLog'
import { InviteForm, InviteResult } from './InviteForm'
import type { MembersState } from './useCompanyMembers'

/** What App's lost-access handler found: still a member, gone (the boundary moved on), or no answer. */
export type AccessCheck = 'kept' | 'gone' | 'failed'

interface Props {
  lang: Lang
  /** The active company, with the user's role in it from the last reconciliation. */
  client: Client
  userId: string
  members: MembersState
  onReloadMembers: () => void
  /** Re-checks the session after a `404` (design, "Lost access"). */
  onAccessLost: (companyId: string) => Promise<AccessCheck>
  /** `reconcile()`: after a `403`, or a role change on the user's own row. */
  onRefreshSession: () => Promise<void>
  /**
   * After a successful leave: the boundary treats the company as lost, without the
   * banner. `false` when the read that should move the switcher on failed.
   */
  onLeft: (companyId: string) => Promise<boolean | void>
  /** After a successful delete; answers like `onLeft` (GRO-131, D6). */
  onDeleted: (companyId: string) => Promise<boolean | void>
  /** A real company's delete freezes it for an admin to restore (GRO-138, D8). */
  deletionHold?: boolean
  /** Below 768px each table becomes stacked rows. */
  narrow?: boolean
}

type Confirm =
  | { kind: 'role'; member: Member; to: Role }
  | { kind: 'remove'; member: Member }
  | { kind: 'leave'; member: Member }
  | { kind: 'block'; member: Member }
  | { kind: 'revoke'; invite: Invite }
  | { kind: 'delete' }

type Pending = { phase: 'loading' } | { phase: 'ready'; invites: Invite[] } | { phase: 'failed' }
type Line = { tone: 'info' | 'warn'; text: string }
type Result = { inviteId: string; email: string; role: Role; token: string }

const BORDER = '1px solid var(--mantine-color-default-border)'
const DAY_MS = 86_400_000

function joinedOn(at: string, lang: Lang): string {
  const locale: Record<Lang, string> = { en: 'en-US', ru: 'ru-RU', uk: 'uk-UA' }
  return new Intl.DateTimeFormat(locale[lang], { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(at))
}

function isExpired(invite: Invite, now: number): boolean {
  return invite.status === 'expired' || Date.parse(invite.expiresAt) <= now
}

const sameEmail = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/** Whether `role` may still confirm an open row after its role changed (design, "The user's own role changes"). */
function stillAllowed(confirm: Confirm, role: Role, isDemo: boolean): boolean {
  switch (confirm.kind) {
    case 'role': return canChangeMember(role, confirm.member.role, confirm.to)
    case 'remove': return canRemoveMember(role, confirm.member.role)
    case 'revoke': return can(role, 'invite') && !(role === 'admin' && confirm.invite.role === 'owner')
    case 'leave':
    case 'block': return true
    case 'delete': return canDeleteCompany(role, isDemo)
  }
}

function confirmText(copy: MembersCopy, confirm: Confirm, clientName: string, isDemo: boolean, deletionHold: boolean): string {
  switch (confirm.kind) {
    case 'role':
      if (confirm.to === 'admin') return fmt(copy.confirmAdmin, { email: confirm.member.email })
      if (confirm.to === 'owner') return fmt(copy.confirmOwner, { email: confirm.member.email })
      return fmt(copy.confirmRole, { email: confirm.member.email, role: copy.roles[confirm.to] })
    case 'remove': return fmt(copy.confirmRemove, { email: confirm.member.email, client: clientName })
    case 'leave': return fmt(copy.confirmLeave, { client: clientName })
    case 'block': return fmt(copy.lastOwner, { client: clientName })
    case 'revoke': return fmt(copy.confirmRevoke, { email: confirm.invite.email })
    case 'delete':
      if (isDemo) return copy.confirmDeleteDemo
      return fmt(deletionHold ? copy.confirmDeleteHold : copy.confirmDelete, { client: clientName })
  }
}

/**
 * The Members page (GRO-106 design, screens 1 and 2): current members, pending invitations,
 * the invite form and the activity log. App mounts it keyed by the company, so nothing here
 * outlives a switch; `alive` drops every answer that arrives after unmount, which is how a
 * response for company A never reaches company B's page or App (evaluation 15a).
 */
export function MembersPage({ lang, client, userId, members, onReloadMembers, onAccessLost, onRefreshSession, onLeft, onDeleted, deletionHold = false, narrow = false }: Props) {
  const copy = MEMBERS_COPY[lang]
  const companyId = client.id
  const role = client.role
  const isDemo = client.isDemo
  const manager = can(role, 'invite')
  const canSee = can(role, 'seePendingInvites')

  const [confirm, setConfirm] = useState<Confirm | null>(null)
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState<Line | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [result, setResult] = useState<Result | null>(null)
  // A revoked or replaced invitation's link must not stay on screen to copy.
  const dropResult = (inviteId: string) => setResult((current) => (current?.inviteId === inviteId ? null : current))
  const [pending, setPending] = useState<Pending | null>(null)
  const [uncertain, setUncertain] = useState<string[]>([])
  const [refreshKey, setRefreshKey] = useState(0)
  // The delete committed but the read that moves the switcher on failed: say so, with a
  // retry of that read, rather than leave the deleted company on screen without a word.
  const [deletedStuck, setDeletedStuck] = useState(false)
  const [deleteUnknown, setDeleteUnknown] = useState(false)

  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])
  const pendingRev = useRef(0)
  const roleRef = useRef(role)
  roleRef.current = role
  const lostRef = useRef(onAccessLost)
  lostRef.current = onAccessLost
  const forbiddenRef = useRef<() => void>(() => {})

  /** One pending-list read; an answer older than the latest read or local change is dropped. */
  const loadPending = useCallback(async (): Promise<Invite[] | null> => {
    const rev = ++pendingRev.current
    const result = await listInvites(companyId)
    if (!alive.current || rev !== pendingRev.current) return null
    switch (result.status) {
      case 'ok': {
        const invites = result.body.filter((i) => i.status === 'pending' || i.status === 'expired')
        setPending({ phase: 'ready', invites })
        return invites
      }
      case 'stale':
      case 'expired':
        return null
      case 'forbidden':
        setPending(null)
        forbiddenRef.current()
        return null
      case 'notFound':
        setPending({ phase: 'failed' })
        void lostRef.current(companyId)
        return null
      default:
        setPending({ phase: 'failed' })
        return null
    }
  }, [companyId])

  // The list exists only while the role may see it; losing that drops it from state.
  useEffect(() => {
    if (canSee) {
      setPending((current) => current ?? { phase: 'loading' })
      void loadPending()
    } else {
      pendingRev.current += 1
      setPending(null)
    }
  }, [canSee, loadPending])

  // A role change closes what the new role no longer allows.
  useEffect(() => {
    if (!can(role, 'invite')) {
      setFormOpen(false)
      setResult(null)
    }
    setConfirm((current) => (current !== null && !stillAllowed(current, role, isDemo) ? null : current))
  }, [role])

  const changed = () => setRefreshKey((n) => n + 1)
  const reloadPending = () => { if (can(roleRef.current, 'seePendingInvites')) void loadPending() }
  const markUncertain = (email: string) => setUncertain((list) => (list.some((e) => sameEmail(e, email)) ? list : [...list, email]))
  const clearUncertain = (email: string) => setUncertain((list) => list.filter((e) => !sameEmail(e, email)))

  const forbidden = () => {
    setLine({ tone: 'warn', text: copy.forbidden })
    setConfirm(null)
    void onRefreshSession()
  }
  forbiddenRef.current = forbidden

  /** A `404` on a write: re-check, and when the caller still belongs, run `kept`. */
  const lost = async (kept: () => void) => {
    setConfirm(null)
    const verdict = await onAccessLost(companyId)
    if (!alive.current) return
    if (verdict === 'kept') kept()
    else if (verdict === 'failed') setLine({ tone: 'warn', text: copy.failed })
  }

  /** The shared tail of a member write that did not succeed. */
  const memberFailure = async (outcome: Outcome<unknown>, member: Member) => {
    switch (outcome.status) {
      case 'stale':
      case 'expired':
        setConfirm(null)
        return
      case 'refused':
        if (outcome.code === 'last_owner') {
          setConfirm({ kind: 'block', member })
          return
        }
        break
      case 'forbidden':
        forbidden()
        return
      case 'notFound':
        await lost(() => {
          onReloadMembers()
          setLine({ tone: 'info', text: fmt(copy.goneMember, { email: member.email }) })
        })
        return
    }
    setConfirm(null)
    setLine({ tone: 'warn', text: copy.failed })
  }

  async function confirmRole(member: Member, to: Role) {
    setBusy(true)
    const outcome = await changeRole(companyId, member.userId, to)
    if (!alive.current) return
    setBusy(false)
    if (outcome.status !== 'ok') return memberFailure(outcome, member)
    setConfirm(null)
    changed()
    onReloadMembers()
    if (member.userId === userId) {
      await onRefreshSession()
      if (!alive.current) return
    }
    reloadPending()
  }

  async function confirmRemove(member: Member) {
    setBusy(true)
    const outcome = await removeMember(companyId, member.userId)
    if (!alive.current) return
    setBusy(false)
    if (outcome.status !== 'ok') return memberFailure(outcome, member)
    setConfirm(null)
    changed()
    onReloadMembers()
    reloadPending()
  }

  async function confirmLeave(member: Member) {
    setBusy(true)
    const outcome = await removeMember(companyId, userId)
    if (!alive.current) return
    setBusy(false)
    if (outcome.status === 'notFound') {
      // The membership is already gone: the state the user asked for.
      setConfirm(null)
      await onLeft(companyId)
      return
    }
    if (outcome.status !== 'ok') return memberFailure(outcome, member)
    setConfirm(null)
    await onLeft(companyId)
  }

  /** The delete is done (or was already): hand over to the boundary, which may open first run. */
  async function afterDelete() {
    setConfirm(null)
    setDeleteUnknown(false)
    const moved = await onDeleted(companyId)
    if (!alive.current) return
    setDeletedStuck(moved === false)
  }

  async function confirmDelete() {
    setBusy(true)
    const outcome = await deleteCompany(companyId)
    if (!alive.current) return
    setBusy(false)
    switch (outcome.status) {
      case 'ok':
      case 'notFound':
        return afterDelete()
      case 'refused':
        if (outcome.code === 'company_deleted') return afterDelete()
        break
      case 'stale':
      case 'expired':
        setConfirm(null)
        return
      case 'forbidden':
        forbidden()
        return
    }
    // No answer that settles it: the delete may have committed. The dialog stays open and
    // its retry sends the same delete, which a committed first attempt answers with 410.
    setDeleteUnknown(true)
  }

  async function resend(invite: Invite) {
    if (busy) return
    setBusy(true)
    setLine(null)
    setConfirm(null)
    const outcome = await resendInvite(companyId, invite.id)
    if (!alive.current) return
    setBusy(false)
    switch (outcome.status) {
      case 'ok': {
        const next = outcome.body.invite
        pendingRev.current += 1
        setPending((current) => (current?.phase === 'ready'
          ? { phase: 'ready', invites: current.invites.map((i) => (i.id === invite.id ? next : i)) }
          : current))
        clearUncertain(invite.email)
        setFormOpen(false)
        setResult({ inviteId: next.id, email: next.email, role: next.role, token: outcome.body.token })
        changed()
        return
      }
      case 'stale':
      case 'expired':
        return
      case 'forbidden':
        forbidden()
        return
      case 'notFound':
        dropResult(invite.id)
        await lost(() => {
          reloadPending()
          setLine({ tone: 'info', text: copy.goneInvite })
        })
        return
      case 'unreachable':
        dropResult(invite.id)
        markUncertain(invite.email)
        reloadPending()
        return
      case 'refused':
        dropResult(invite.id)
        reloadPending()
        setLine({ tone: 'info', text: copy.goneInvite })
        return
      case 'unavailable':
        setLine({ tone: 'warn', text: copy.failed })
    }
  }

  async function confirmRevoke(invite: Invite) {
    setBusy(true)
    const outcome = await revokeInvite(companyId, invite.id)
    if (!alive.current) return
    setBusy(false)
    switch (outcome.status) {
      case 'ok':
        setConfirm(null)
        dropResult(invite.id)
        pendingRev.current += 1
        setPending((current) => (current?.phase === 'ready'
          ? { phase: 'ready', invites: current.invites.filter((i) => i.id !== invite.id) }
          : current))
        clearUncertain(invite.email)
        changed()
        return
      case 'stale':
      case 'expired':
        setConfirm(null)
        return
      case 'forbidden':
        forbidden()
        return
      case 'notFound':
        dropResult(invite.id)
        await lost(() => {
          reloadPending()
          setLine({ tone: 'info', text: copy.goneInvite })
        })
        return
      default:
        dropResult(invite.id)
        setConfirm(null)
        setLine({ tone: 'warn', text: copy.failed })
    }
  }

  function runConfirm(open: Confirm) {
    if (busy) return
    setLine(null)
    switch (open.kind) {
      case 'role': void confirmRole(open.member, open.to); return
      case 'remove': void confirmRemove(open.member); return
      case 'leave': void confirmLeave(open.member); return
      case 'revoke': void confirmRevoke(open.invite); return
      case 'delete': void confirmDelete(); return
      case 'block': setConfirm(null)
    }
  }

  const list = members.phase === 'ready' ? members.members : []
  const owners = list.filter((m) => m.role === 'owner').length

  function askLeave(member: Member) {
    setLine(null)
    // The member list already proves the user is the only Owner: no request.
    setConfirm(member.role === 'owner' && owners <= 1 ? { kind: 'block', member } : { kind: 'leave', member })
  }

  function confirmRow(open: Confirm): ReactNode {
    const label = open.kind === 'role' ? copy.changeRoleOk
      : open.kind === 'remove' ? copy.removeOk
        : open.kind === 'leave' ? copy.leaveOk
          : open.kind === 'revoke' ? copy.revoke
            : open.kind === 'delete' ? (deleteUnknown ? copy.retry : copy.deleteOk) : null
    return (
      <Group justify="space-between" gap="8px 16px" wrap="wrap" role="group" data-testid={`confirm-${open.kind}`}>
        <Stack gap={4} style={{ flex: '1 1 280px' }}>
          <Text fz="sm" style={{ textWrap: 'pretty' }}>{confirmText(copy, open, client.name, isDemo, deletionHold)}</Text>
          {open.kind === 'delete' && deleteUnknown && (
            <Text fz="sm" role="alert" data-testid="delete-unknown">{copy.deleteUnknown}</Text>
          )}
        </Stack>
        <Group gap="xs">
          {label !== null && (
            <Button size="compact-sm" color={open.kind === 'role' ? undefined : 'bad'} disabled={busy} onClick={() => runConfirm(open)}>
              {label}
            </Button>
          )}
          <Button size="compact-sm" variant="default" onClick={() => { setConfirm(null); setDeleteUnknown(false) }}>
            {open.kind === 'block' ? copy.close : copy.cancel}
          </Button>
        </Group>
      </Group>
    )
  }

  const openFor = (key: string): Confirm | null => {
    if (confirm === null) return null
    if (confirm.kind === 'delete') return null
    const id = confirm.kind === 'revoke' ? confirm.invite.id : confirm.member.userId
    return id === key ? confirm : null
  }

  function roleCell(member: Member): ReactNode {
    const open = openFor(member.userId)
    if (canChangeMember(role, member.role)) {
      return (
        <Select
          aria-label={`${copy.role}: ${member.email}`}
          data={invitableRoles(role).map((value) => ({ value, label: copy.roles[value] }))}
          value={open?.kind === 'role' ? open.to : member.role}
          onChange={(value) => {
            if (value === null || value === member.role) return
            setLine(null)
            setConfirm({ kind: 'role', member, to: value as Role })
          }}
          allowDeselect={false}
          disabled={busy}
          w={{ base: '100%', sm: 170 }}
        />
      )
    }
    return (
      <Stack gap={4} align="flex-start">
        <Tag>{copy.roles[member.role]}</Tag>
        {role === 'admin' && member.role === 'owner' && <Text fz="xs" className="grv-muted">{copy.ownerNote}</Text>}
      </Stack>
    )
  }

  function memberActions(member: Member): ReactNode {
    if (member.userId === userId) {
      return <Button size="compact-sm" variant="default" disabled={busy} onClick={() => askLeave(member)}>{copy.leave}</Button>
    }
    if (canRemoveMember(role, member.role)) {
      return (
        <Button size="compact-sm" variant="subtle" color="bad" disabled={busy} onClick={() => { setLine(null); setConfirm({ kind: 'remove', member }) }}>
          {copy.remove}
        </Button>
      )
    }
    return null
  }

  function who(member: Member): ReactNode {
    return (
      <Group gap={8} wrap="nowrap">
        <Text component="span" fw={500} style={{ overflowWrap: 'anywhere' }}>{member.email}</Text>
        {member.userId === userId && <Tag color="brand">{copy.you}</Tag>}
      </Group>
    )
  }

  function membersTable(): ReactNode {
    if (narrow) {
      return (
        <Box data-testid="members-list" style={{ border: BORDER, borderRadius: 'var(--mantine-radius-lg)', overflow: 'hidden' }}>
          {list.map((member, index) => {
            const open = openFor(member.userId)
            return (
              <Stack key={member.userId} gap={8} px={16} py={12} style={{ borderTop: index === 0 ? 'none' : BORDER }} data-testid="member-row">
                {who(member)}
                <Group gap="8px 12px" wrap="wrap">
                  {roleCell(member)}
                  <Text fz="sm" className="grv-muted">{fmt(copy.joinedOn, { date: joinedOn(member.joinedAt, lang) })}</Text>
                </Group>
                {memberActions(member)}
                {open !== null && confirmRow(open)}
              </Stack>
            )
          })}
        </Box>
      )
    }
    return (
      <Box data-testid="members-list" style={{ border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th><span className="grv-label">{copy.email}</span></Table.Th>
              <Table.Th><span className="grv-label">{copy.role}</span></Table.Th>
              <Table.Th><span className="grv-label">{copy.joinedCol}</span></Table.Th>
              <Table.Th><span className="grv-label">{copy.actions}</span></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {list.map((member) => {
              const open = openFor(member.userId)
              return (
                <Fragment key={member.userId}>
                  <Table.Tr data-testid="member-row">
                    <Table.Td>{who(member)}</Table.Td>
                    <Table.Td>{roleCell(member)}</Table.Td>
                    <Table.Td><span className="grv-num grv-muted">{joinedOn(member.joinedAt, lang)}</span></Table.Td>
                    <Table.Td>{memberActions(member)}</Table.Td>
                  </Table.Tr>
                  {open !== null && (
                    <Table.Tr><Table.Td colSpan={4}>{confirmRow(open)}</Table.Td></Table.Tr>
                  )}
                </Fragment>
              )
            })}
          </Table.Tbody>
        </Table>
      </Box>
    )
  }

  function inviteStatus(invite: Invite, now: number): ReactNode {
    if (isExpired(invite, now)) return <Tag color="warn">{copy.expired}</Tag>
    const days = Math.max(1, Math.ceil((Date.parse(invite.expiresAt) - now) / DAY_MS))
    return <Text fz="sm" className="grv-muted">{fmt(copy.expiresIn, { n: days })}</Text>
  }

  function inviteActions(invite: Invite): ReactNode {
    // The backend's ceiling applies to resend and revoke: an Admin acts on no Owner invitation.
    if (role === 'admin' && invite.role === 'owner') return null
    return (
      <Group gap="xs" wrap="nowrap">
        <Button size="compact-sm" variant="default" disabled={busy} onClick={() => { void resend(invite) }}>{copy.newLink}</Button>
        <Button size="compact-sm" variant="subtle" color="bad" disabled={busy} onClick={() => { setLine(null); setConfirm({ kind: 'revoke', invite }) }}>
          {copy.revoke}
        </Button>
      </Group>
    )
  }

  function pendingSection(): ReactNode {
    if (pending === null) return null
    if (pending.phase === 'loading') return null
    if (pending.phase === 'failed') {
      return (
        <Group gap="xs" role="alert">
          <Text className="grv-muted">{copy.loadFailed}</Text>
          <Button size="compact-sm" variant="default" onClick={() => { void loadPending() }}>{copy.retry}</Button>
        </Group>
      )
    }
    if (pending.invites.length === 0) return null
    const now = Date.now()
    return (
      <Stack gap={12} data-testid="pending-invites">
        <Text component="h3" className="grv-label" m={0}>{`${copy.pending} · ${pending.invites.length}`}</Text>
        <Box style={{ border: BORDER, borderRadius: 'var(--mantine-radius-lg)', overflow: 'hidden' }}>
          {pending.invites.map((invite, index) => {
            const open = openFor(invite.id)
            const unsure = uncertain.some((e) => sameEmail(e, invite.email))
            return (
              <Stack key={invite.id} gap={8} px={16} py={12} style={{ borderTop: index === 0 ? 'none' : BORDER }} data-testid="invite-row" data-invite-id={invite.id}>
                <Group justify="space-between" gap="8px 16px" wrap={narrow ? 'wrap' : 'nowrap'}>
                  <Stack gap={4} style={{ minWidth: 0 }}>
                    <Group gap={8} wrap="wrap">
                      <Text component="span" fw={500} style={{ overflowWrap: 'anywhere' }}>{invite.email}</Text>
                      <Tag>{copy.roles[invite.role]}</Tag>
                    </Group>
                    <Group gap="4px 12px" wrap="wrap">
                      <Text fz="sm" className="grv-muted" style={{ overflowWrap: 'anywhere' }}>{fmt(copy.invitedBy, { email: invite.inviterEmail })}</Text>
                      {inviteStatus(invite, now)}
                    </Group>
                  </Stack>
                  {inviteActions(invite)}
                </Group>
                {unsure && <Text fz="sm" role="status">{copy.uncertain}</Text>}
                {open !== null && confirmRow(open)}
              </Stack>
            )
          })}
        </Box>
      </Stack>
    )
  }

  async function alreadyInvited(email: string): Promise<Invite | null> {
    const invites = await loadPending()
    return invites?.find((i) => sameEmail(i.email, email)) ?? null
  }

  function uncertainCreate(email: string) {
    setFormOpen(false)
    markUncertain(email)
    void loadPending().then((invites) => {
      if (alive.current && invites !== null && !invites.some((i) => sameEmail(i.email, email))) {
        setLine({ tone: 'warn', text: copy.failed })
      }
    })
  }

  return (
    <Stack gap={24} data-testid="members-page" p={{ base: 16, sm: 24 }}>
      <Group justify="space-between" align="flex-start" gap="12px 16px" wrap="wrap">
        <Stack gap={4}>
          <Title order={2} fz="xl">{copy.members}</Title>
          <Text className="grv-muted" style={{ overflowWrap: 'anywhere' }}>{client.name}</Text>
        </Stack>
        {manager && !formOpen && (
          <Button onClick={() => { setResult(null); setLine(null); setFormOpen(true) }}>{copy.invite}</Button>
        )}
      </Group>

      {!manager && <Text className="grv-muted">{copy.readOnly}</Text>}

      {manager && formOpen && (
        <InviteForm
          lang={lang}
          companyId={companyId}
          actor={role}
          onCreated={(invite, token) => {
            setFormOpen(false)
            setResult({ inviteId: invite.id, email: invite.email, role: invite.role, token })
            clearUncertain(invite.email)
            changed()
            reloadPending()
          }}
          onAlreadyInvited={alreadyInvited}
          onResend={(invite) => { void resend(invite) }}
          onUncertain={uncertainCreate}
          onForbidden={() => { void onRefreshSession() }}
          onNotFound={() => { void lost(() => setLine({ tone: 'warn', text: copy.failed })) }}
          onCancel={() => setFormOpen(false)}
        />
      )}

      {manager && result !== null && (
        <InviteResult lang={lang} email={result.email} role={result.role} token={result.token} onDone={() => setResult(null)} />
      )}

      {line !== null && (
        <Box role={line.tone === 'warn' ? 'alert' : 'status'} data-testid="members-line">
          <AlertBanner tone={line.tone}>{line.text}</AlertBanner>
        </Box>
      )}

      {members.phase === 'loading' && <Text className="grv-muted" role="status">{copy.loading}</Text>}
      {members.phase === 'failed' && (
        <Group gap="xs" role="alert">
          <Text className="grv-muted">{copy.loadFailed}</Text>
          <Button size="compact-sm" variant="default" onClick={onReloadMembers}>{copy.retry}</Button>
        </Group>
      )}
      {members.phase === 'ready' && membersTable()}

      {canSee && pendingSection()}

      {can(role, 'readActivity') && (
        <ActivityLog
          companyId={companyId}
          lang={lang}
          refreshKey={refreshKey}
          onAccessLost={(id) => { void onAccessLost(id) }}
        />
      )}

      {deletedStuck && (
        <Group gap="xs" role="alert" data-testid="deleted-stuck">
          <Text>{fmt(copy.deletedRefreshFailed, { client: client.name })}</Text>
          <Button size="compact-sm" variant="default" onClick={() => { void afterDelete() }}>{copy.retry}</Button>
        </Group>
      )}
      {canDeleteCompany(role, isDemo) && !deletedStuck && (
        confirm?.kind === 'delete'
          ? confirmRow(confirm)
          : (
            <Box>
              <Button color="bad" variant="outline" disabled={busy} onClick={() => { setLine(null); setDeleteUnknown(false); setConfirm({ kind: 'delete' }) }}>
                {isDemo ? copy.deleteDemoCompany : copy.deleteCompany}
              </Button>
            </Box>
          )
      )}
    </Stack>
  )
}
